import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { calculateLevelBasedPoints, POINTS } from '../utils/levelCalculation';
import type { Quest, QuestFolder, QuestGoal, PointRecord } from '../types';
import { useActivityLogStore } from './activityLogStore';
import { useUpgradesStore } from './upgradesStore';
import { autoCheckInPacts } from '../utils/pactAutoCheckIn';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { dedupeInit, isCloudFresh, markCloudFresh } from '../lib/cloudBoot';
import {
  accountGeneration, accountStorage, cachedUserIdOwner, scopeToAccount, swappingAccount,
} from '../lib/accountStorage';
import {
  createBlobSync, mergeCollection, mergeTombstones, pruneTombstones, tombstone, sameRecords,
  matchesCloud, sameTombstones,
  type Tombstones,
} from '../lib/syncBlob';
import { supabaseBlobTransport } from '../lib/syncBlobTransport';
import { deriveLinkedGoalState, type LinkedGoalSources } from '../utils/questGoalLink';
import { reopenQuest } from '../utils/questReopen';
import { useHabitStore } from './habitStore';
import { useTaskStore } from './taskStore';
import { useProjectStore } from './projectStore';
import { useRoutineStore } from './routineStore';
import { useProgramStore } from './programStore';
import { useEventStore } from './eventStore';
import { useAbilitiesStore } from './abilitiesStore';

// Read the source now: a completed goal's stored progress is 100 even when its
// link has since regressed. Explicit undo must not reuse that stale value.
function undoneGoalProgress(goal: QuestGoal): number {
  if (!goal.linkType || !goal.linkId) return 0;
  const derived = deriveLinkedGoalState(goal, {
    habits: useHabitStore.getState().habits,
    tasksByCategory: useTaskStore.getState().tasks,
    projects: useProjectStore.getState().projects,
    routines: useRoutineStore.getState().routines,
    events: useEventStore.getState().events,
    programRuns: useProgramStore.getState().initiations,
  });
  const ability = goal.linkType === 'ability'
    ? useAbilitiesStore.getState().abilities.find(item => item.id === goal.linkId)
    : undefined;
  const progress = derived?.progress ?? (ability ? Math.round(ability.level / Math.max(1, goal.quantity) * 100) : 0);
  return Math.min(99, Math.max(0, progress));
}

// ─── Supabase sync (sync epic) ──────────────────────────────────────────────
// Quests + folders sync as ONE atomic blob per user (user_quests.data =
// {folders, quests}). Whole-store-blob (not row-per-item) so the folder/quest
// cross-references stay consistent across devices (default folders are seeded
// with random ids). A single store subscription auto-syncs on any data change,
// guarded so initialize()'s own load doesn't echo back. Fire-and-forget.

const USER_QUESTS_TABLE = 'user_quests';
function readTombstones(data: any): Tombstones {
  const raw = data?.deleted;
  return raw && typeof raw === 'object' ? (raw as Tombstones) : {};
}

// ⚠️ Read-modify-write, NOT a wholesale upsert. The old code PUT the entire local
// {folders, quests}, so a device holding a stale cache silently deleted records it
// had never seen (diagnosed 2026-07-27 on the inbox — see lib/syncBlob.ts).
// Folder and quest ids are both UUIDs, so ONE tombstone map covers both collections.
const questSync = createBlobSync({
  transport: supabaseBlobTransport(USER_QUESTS_TABLE),
  owner: () => useQuestStore.getState().cachedUserId,
  onError: err => console.error('Failed to sync quests to Supabase:', err),
  build: (cloud) => {
    const { folders, quests, deleted } = useQuestStore.getState();
    const cloudTombs = readTombstones(cloud);
    const merged = pruneTombstones(mergeTombstones(deleted, cloudTombs));
    const nextFolders = mergeCollection(folders, Array.isArray(cloud?.folders) ? cloud.folders : [], merged);
    const nextQuests = mergeCollection(quests, Array.isArray(cloud?.quests) ? cloud.quests : [], merged);
    if (matchesCloud([[nextFolders, cloud?.folders], [nextQuests, cloud?.quests]], merged, cloudTombs)) return null;
    return { folders: nextFolders, quests: nextQuests, deleted: merged };
  },
});

/**
 * The quest's next updatedAt: now, but always after the stamp it replaces, so two
 * changes in the same millisecond still read as a change to the blob merge (which
 * keeps the cloud copy on a tie). See the note above the sync subscription.
 */
export function touchQuest(quest: { updatedAt?: string }): string {
  const previous = quest.updatedAt ? Date.parse(quest.updatedAt) : Number.NaN;
  const now = Date.now();
  return new Date(Number.isFinite(previous) && previous >= now ? previous + 1 : now).toISOString();
}

const restoresCompleted = (quest: Quest) =>
  !!quest.pointRecord && quest.goals.length > 0 && quest.goals.every(goal => goal.completed);

interface QuestState {
  folders: QuestFolder[];
  quests: Quest[];
  /** id → deletedAt (folders AND quests). Without it the union-on-write resurrects deletes. */
  deleted: Tombstones;
  userLevel: number;
  initializedUserId: string | null;
  /**
   * The last load failed — Growth Foundations says "couldn't check" (and offers Retry) instead of
   * "checking". Not saved: a reload loads again.
   */
  loadFailed: boolean;
  cachedUserId: string | null;
  initialize: () => Promise<void>;

  // Folder operations
  addFolder: (folder: Omit<QuestFolder, 'id' | 'createdAt' | 'updatedAt' | 'order'>) => string;
  updateFolder: (id: string, updates: Partial<QuestFolder>) => void;
  deleteFolder: (id: string) => void;
  reorderFolders: (sourceIndex: number, targetIndex: number) => void;
  
  // Quest operations
  addQuest: (quest: Omit<Quest, 'id' | 'createdAt' | 'updatedAt' | 'goals' | 'totalXP' | 'earnedXP' | 'progress'>) => string;
  updateQuest: (id: string, updates: Partial<Quest>) => void;
  deleteQuest: (id: string) => void;
  moveQuest: (questId: string, targetFolderId: string) => void;
  completeQuest: (id: string) => void;
  archiveQuest: (id: string) => void;
  restoreQuest: (id: string) => void;
  
  // Goal operations
  addGoal: (questId: string, goal: Omit<QuestGoal, 'id' | 'questId' | 'createdAt' | 'updatedAt' | 'completed' | 'progress'>) => string;
  updateGoal: (questId: string, goalId: string, updates: Partial<QuestGoal>) => void;
  deleteGoal: (questId: string, goalId: string) => void;
  toggleGoal: (questId: string, goalId: string) => void;
  updateGoalProgress: (questId: string, goalId: string, progress: number) => void;
  /**
   * Reconciles LINKED goals against their linked entity's real state, so a habit
   * completed from the Habits page or Today's Actions advances its quest exactly as
   * completing it from inside the quest does. Only ever advances — never un-completes
   * and never claws back XP. Safe to call repeatedly (idempotent).
   */
  syncLinkedGoals: (sources: LinkedGoalSources) => void;

  // Getters
  getQuestsByFolder: (folderId: string) => Quest[];
  getActiveQuests: () => Quest[];
  getCompletedQuests: () => Quest[];
  getArchivedQuests: () => Quest[];
  
  // User level
  setUserLevel: (level: number) => void;
}

// The four built-in quest folders. STABLE ids + isSystem so they're never
// duplicated: every device/session that re-seeds resolves to the SAME records,
// and the merge-by-id sync collapses them instead of stacking new random-id
// copies (the bug that produced "Main Quests ×3" etc.).
const SYSTEM_FOLDER_EPOCH = '1970-01-01T00:00:00.000Z';
const SYSTEM_FOLDERS: QuestFolder[] = [
  { id: 'system-main-quests', name: 'Main Quests', description: 'Primary objectives and life-changing goals', color: '#3b82f6', icon: 'Trophy', isSystem: true, order: 0, createdAt: SYSTEM_FOLDER_EPOCH, updatedAt: SYSTEM_FOLDER_EPOCH },
  { id: 'system-side-quests', name: 'Side Quests', description: 'Secondary objectives and personal projects', color: '#8b5cf6', icon: 'Star', isSystem: true, order: 1, createdAt: SYSTEM_FOLDER_EPOCH, updatedAt: SYSTEM_FOLDER_EPOCH },
  { id: 'system-daily-quests', name: 'Daily Quests', description: 'Regular tasks and recurring objectives', color: '#22c55e', icon: 'Calendar', isSystem: true, order: 2, createdAt: SYSTEM_FOLDER_EPOCH, updatedAt: SYSTEM_FOLDER_EPOCH },
  { id: 'system-errands', name: 'Errands', description: 'Quick tasks and small objectives', color: '#f59e0b', icon: 'CheckSquare', isSystem: true, order: 3, createdAt: SYSTEM_FOLDER_EPOCH, updatedAt: SYSTEM_FOLDER_EPOCH },
];

/**
 * Ensure exactly one of each built-in folder exists (by stable id), collapsing
 * any duplicates — folders sharing a stable id OR a canonical name are merged
 * into the canonical record and their quests re-pointed at it. Idempotent, so
 * it's safe to run on every rehydrate/load. Only a changed quest folder is touched.
 */
export function reconcileQuestFolders(
  folders: QuestFolder[],
  quests: Quest[],
): { folders: QuestFolder[]; quests: Quest[]; changed: boolean } {
  let working = [...folders];
  let workingQuests = quests;
  let changed = false;

  for (const sys of SYSTEM_FOLDERS) {
    const matches = working.filter((f) => f.id === sys.id || f.name === sys.name);

    if (matches.length === 0) {
      working.push({ ...sys });
      changed = true;
      continue;
    }
    if (matches.length === 1 && matches[0].id === sys.id) {
      continue; // already canonical
    }

    // Collapse: repoint every matched folder's quests at the canonical id…
    const matchIds = new Set(matches.map((f) => f.id));
    workingQuests = workingQuests.map((q) =>
      matchIds.has(q.folderId) && q.folderId !== sys.id
        ? { ...q, folderId: sys.id, updatedAt: touchQuest(q) } : q,
    );
    // …then drop the matched folders and re-add the single canonical record.
    const order = matches.find((f) => f.id === sys.id)?.order ?? matches[0].order;
    working = working.filter((f) => !matchIds.has(f.id));
    working.push({ ...sys, order });
    changed = true;
  }

  return { folders: working, quests: workingQuests, changed };
}

// Helper function to calculate quest progress and XP
// Formula: Total XP = (GoalBaseXP + QuestPerGoalBonus) * N + QuestBaseBonus
// Simplified at level 1: Total XP = (50 + 50) * N + 100 = 100*N + 100
function calculateQuestProgress(goals: QuestGoal[], userLevel: number = 1, questPointRecord?: PointRecord): { progress: number; earnedXP: number; totalXP: number } {
  if (goals.length === 0) {
    return { progress: 0, earnedXP: questPointRecord?.points ?? 0, totalXP: 0 };
  }

  const completedGoals = goals.filter(goal => goal.completed);
  const progress = Math.round((completedGoals.length / goals.length) * 100);

  // Earned stays earned after undo; missing legacy records are not new awards.
  const goalsEarnedXP = goals.reduce((sum, goal) => sum + (goal.pointRecord?.points ?? 0), 0);

  // Add quest completion bonus if present
  const questBonusXP = questPointRecord?.points || 0;
  const earnedXP = goalsEarnedXP + questBonusXP;

  // Calculate total possible XP using new formula
  // Per goal: QUEST_GOAL_BASE (50) + QUEST_PER_GOAL_BONUS (50) = 100 per goal
  // Plus: QUEST_BASE_BONUS (100) for quest completion
  const goalCount = goals.length;
  const perGoalXP = calculateLevelBasedPoints(
    POINTS.QUEST_GOAL_BASE + POINTS.QUEST_PER_GOAL_BONUS,
    userLevel
  );
  const baseBonus = calculateLevelBasedPoints(POINTS.QUEST_BASE_BONUS, userLevel);
  const totalXP = (perGoalXP * goalCount) + baseBonus;

  return { progress, earnedXP, totalXP };
}

export const useQuestStore = create<QuestState>()(
  persist(
    (set, get) => ({
      folders: [],
      quests: [],
      userLevel: 1,
      deleted: {},
      initializedUserId: null,
      loadFailed: false,
      cachedUserId: null,

      // Load-on-login from the user_quests blob, keyed by user id (re-runs on
      // account switch). Cloud-wins when present (atomic snapshot replace); else
      // keep this user's local data (or reset on a different account). The set is
      // guarded so the store subscription doesn't echo the load back to cloud.
      initialize: () => dedupeInit('user_quests', async () => {
        const at = accountGeneration();
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;
          if (get().initializedUserId === user.id) return;
          if (get().loadFailed) set({ loadFailed: false });
          // Cache-first boot: while this user's local cache is fresh, skip the
          // cloud read entirely (write-through keeps cloud current meanwhile).
          if (get().cachedUserId === user.id && isCloudFresh('user_quests', user.id)) {
            set({ initializedUserId: user.id });
            return;
          }

          const { data, error } = await supabase
            .from(USER_QUESTS_TABLE)
            .select('*');
          // An answer for an account that has since signed out is not this copy's.
          if (at !== accountGeneration()) return;
          if (error) throw error;

          const row = (data ?? [])[0];
          const state = get();
          const cacheIsOurs =
            state.cachedUserId === null || state.cachedUserId === user.id;

          // Resolve the post-load folders/quests…
          let mergedFolders: QuestFolder[];
          let mergedQuests: Quest[];
          let mergedDeleted: Tombstones = cacheIsOurs ? state.deleted : {};
          if (row && row.data && Array.isArray(row.data.quests)) {
            // Item-level merge (newer updatedAt wins) so concurrent edits on
            // another device aren't clobbered. Don't seed local for a
            // different account (anti-bleed).
            mergedDeleted = pruneTombstones(mergeTombstones(
              cacheIsOurs ? state.deleted : {}, readTombstones(row.data)));
            mergedFolders = mergeCollection(cacheIsOurs ? state.folders : [], Array.isArray(row.data.folders) ? row.data.folders : [], mergedDeleted);
            mergedQuests = mergeCollection(cacheIsOurs ? state.quests : [], row.data.quests, mergedDeleted);
          } else if (cacheIsOurs) {
            mergedFolders = state.folders;
            mergedQuests = state.quests;
          } else {
            mergedFolders = [];
            mergedQuests = [];
          }

          // …then collapse any duplicate built-in folders (the dup-folder fix).
          const reconciled = reconcileQuestFolders(mergedFolders, mergedQuests);

          // ⚠️ reconcileQuestFolders DROPS duplicate built-in folders. Writes are a
          // union now, so a dropped folder would come straight back on the next
          // merge unless the collapse is recorded as a real deletion.
          const keptFolderIds = new Set(reconciled.folders.map(f => f.id));
          let deleted = mergedDeleted;
          for (const f of mergedFolders) {
            if (!keptFolderIds.has(f.id)) deleted = tombstone(deleted, f.id);
          }

          set({
            folders: sameRecords(reconciled.folders, state.folders) ? state.folders : reconciled.folders,
            quests: sameRecords(reconciled.quests, state.quests) ? state.quests : reconciled.quests,
            deleted: sameTombstones(deleted, state.deleted) ? state.deleted : deleted,
            initializedUserId: user.id,
            cachedUserId: user.id,
          });
          // The reconciliation — or records the row lacks: written when the cloud lacks them.
          questSync.writeBack(row?.data);
          markCloudFresh('user_quests', user.id);
        } catch (error) {
          console.error('Failed to initialize quests from Supabase:', error);
          // ⚠️ Only this account's: a load that started before a sign-out says nothing about the next.
          if (at === accountGeneration()) set({ loadFailed: true });
        }
      }),

      setUserLevel: (level) => set({ userLevel: level }),
      
      // Folder operations
      addFolder: (folderData) => {
        const id = crypto.randomUUID();
        const folder: QuestFolder = {
          ...folderData,
          id,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          order: get().folders.length
        };
        
        set((state) => ({
          folders: [...state.folders, folder]
        }));
        
        return id;
      },
      
      updateFolder: (id, updates) => {
        const folder = get().folders.find(f => f.id === id);
        if (folder?.isSystem) {
          const { name, icon, isSystem, ...allowedUpdates } = updates;
          set((state) => ({
            folders: state.folders.map((f) =>
              f.id === id
                ? {
                    ...f,
                    ...allowedUpdates,
                    updatedAt: new Date().toISOString()
                  }
                : f
            )
          }));
        } else {
          set((state) => ({
            folders: state.folders.map((f) =>
              f.id === id
                ? {
                    ...f,
                    ...updates,
                    updatedAt: new Date().toISOString()
                  }
                : f
            )
          }));
        }
      },
      
      deleteFolder: (id) => {
        const folder = get().folders.find(f => f.id === id);
        if (folder?.isSystem) return;

        // Check if folder has quests
        const folderQuests = get().quests.filter(quest => quest.folderId === id);

        if (folderQuests.length > 0) {
          // Move quests to the first available folder or delete them
          const otherFolders = get().folders.filter(folder => folder.id !== id);
          const targetFolderId = otherFolders.length > 0 ? otherFolders[0].id : null;

          if (targetFolderId) {
            // Move quests to another folder
            set((state) => ({
              quests: state.quests.map(quest =>
                quest.folderId === id
                  ? { ...quest, folderId: targetFolderId, updatedAt: touchQuest(quest) }
                  : quest
              )
            }));
          } else {
            // Delete quests if no other folder exists
            set((state) => ({
              quests: state.quests.filter(quest => quest.folderId !== id),
              deleted: state.quests
                .filter(quest => quest.folderId === id)
                .reduce((acc, quest) => tombstone(acc, quest.id), state.deleted),
            }));
          }
        }

        // Delete the folder. ⚠️ Needs a tombstone — writes are a union now, so a
        // bare filter would be undone by the next merge against the cloud copy.
        set((state) => ({
          folders: state.folders.filter((folder) => folder.id !== id),
          deleted: tombstone(state.deleted, id),
        }));
      },
      
      reorderFolders: (sourceIndex, targetIndex) => {
        set((state) => {
          const newFolders = [...state.folders];
          const [removed] = newFolders.splice(sourceIndex, 1);
          newFolders.splice(targetIndex, 0, removed);
          
          // Update order property
          return {
            folders: newFolders.map((folder, index) => ({
              ...folder,
              order: index
            }))
          };
        });
      },
      
      // Quest operations
      addQuest: (questData) => {
        const id = crypto.randomUUID();
        const quest: Quest = {
          ...questData,
          id,
          createdAt: new Date().toISOString(),
          updatedAt: touchQuest({}),
          goals: [],
          totalXP: 0,
          earnedXP: 0,
          progress: 0
        };
        
        set((state) => ({
          quests: [...state.quests, quest]
        }));
        
        return id;
      },
      
      updateQuest: (id, updates) => {
        set((state) => ({
          quests: state.quests.map((quest) =>
            quest.id === id
              ? { 
                  ...quest, 
                  ...updates,
                  updatedAt: touchQuest(quest)
                }
              : quest
          )
        }));
      },
      
      deleteQuest: (id) => {
        set((state) => ({
          quests: state.quests.filter((quest) => quest.id !== id),
          deleted: tombstone(state.deleted, id),
        }));
      },

      moveQuest: (questId, targetFolderId) => {
        const targetFolder = get().folders.find(f => f.id === targetFolderId);
        if (!targetFolder) return;

        set((state) => ({
          quests: state.quests.map((quest) =>
            quest.id === questId
              ? { ...quest, folderId: targetFolderId, updatedAt: touchQuest(quest) }
              : quest
          )
        }));

        const movedQuest = get().quests.find(q => q.id === questId);
        if (movedQuest) {
          useActivityLogStore.getState().addActivity({
            activityType: 'quest',
            activityName: movedQuest.title,
            domainId: movedQuest.category,
            xpEarned: 0,
            metadata: {
              questId: movedQuest.id,
              action: 'moved',
              targetFolder: targetFolder.name
            },
            completedAt: new Date().toISOString()
          });
        }
      },

      completeQuest: (id) => {
        const quest = get().quests.find(q => q.id === id);
        if (!quest || quest.status === 'archived') return;

        const allGoalsCompleted = quest.goals.every(goal => goal.completed);
        if (!allGoalsCompleted) return;

        const hadPointRecord = !!quest.pointRecord;

        set((state) => {
          // Calculate quest completion bonus: BaseBonus only
          // (Per-goal bonuses are already included in goal completion XP)
          const baseBonus = calculateLevelBasedPoints(POINTS.QUEST_BASE_BONUS, state.userLevel);
          const completionPoints = baseBonus;

          const pointRecord = !quest.pointRecord ? {
            points: completionPoints,
            userLevel: state.userLevel,
            earnedAt: new Date().toISOString()
          } : quest.pointRecord;

          // Recalculate quest progress with the new pointRecord
          const { progress, earnedXP, totalXP } = calculateQuestProgress(quest.goals, state.userLevel, pointRecord);

          return {
            quests: state.quests.map((q) =>
              q.id === id
                ? {
                    ...q,
                    status: 'completed',
                    completedAt: q.status === 'completed' ? (q.completedAt ?? new Date().toISOString()) : new Date().toISOString(),
                    pointRecord,
                    progress,
                    earnedXP,
                    totalXP,
                    updatedAt: touchQuest(q),
                  }
                : q
            )
          };
        });

        if (!hadPointRecord) {
          // pacted quest? publish the pact check-in (silent no-op otherwise)
          void autoCheckInPacts({ type: 'quest', id });
          const completedQuest = get().quests.find(q => q.id === id);
          if (completedQuest && completedQuest.pointRecord) {
            useActivityLogStore.getState().addActivity({
              activityType: 'quest',
              activityName: completedQuest.title,
              domainId: completedQuest.category,
              xpEarned: completedQuest.pointRecord.points,
              metadata: {
                questId: completedQuest.id,
                questCompleted: true,
                totalGoals: completedQuest.goals.length
              },
              completedAt: completedQuest.completedAt || new Date().toISOString()
            });

            useUpgradesStore.getState().checkQuestUnlocks(completedQuest.id);
          }
        }
      },
      
      archiveQuest: (id) => {
        set((state) => ({
          quests: state.quests.map((quest) =>
            quest.id === id
              ? { ...quest, status: 'archived', updatedAt: touchQuest(quest) }
              : quest
          )
        }));
      },
      
      restoreQuest: (id) => {
        set((state) => ({
          quests: state.quests.map((quest) =>
            quest.id === id
              ? {
                  ...quest,
                  // Completed only if it was really completed (its bonus is recorded) and
                  // every goal is still done. An all-done quest that was never completed
                  // comes back active, so its Complete button can still pay the bonus once.
                  status: restoresCompleted(quest) ? 'completed' : 'active',
                  completedAt: restoresCompleted(quest) ? quest.completedAt : undefined,
                  updatedAt: touchQuest(quest),
                }
              : quest
          )
        }));
      },
      
      // Goal operations
      addGoal: (questId, goalData) => {
        const id = crypto.randomUUID();
        const now = new Date().toISOString();
        const goal: QuestGoal = {
          ...goalData,
          id,
          questId,
          completed: false,
          progress: 0,
          createdAt: now,
          updatedAt: now,
          /**
           * Linked goals derive progress from the linked entity, counted from here.
           *
           * ⚠️⚠️ **A CALLER THAT SUPPLIES ONE IS OBEYED.** This used to stamp `now`
           * unconditionally, which silently discarded a window the caller had
           * worked out. A Growth Profile quest counts from the day its RANK opened
           * (D21), so applying one three days after that rank opened would have
           * started the count at the press and quietly dropped three days of
           * qualifying work — while the profile's own tab counted them, because it
           * reads the window directly. Two surfaces, one goal, different answers.
           * ⚠️ Callers that pass nothing still get `now`, exactly as before.
           */
          ...(goalData.linkType && goalData.linkId
            ? { linkStartedAt: goalData.linkStartedAt ?? now } : {})
        };
        
        set((state) => {
          // Add goal to quest
          const updatedQuests = state.quests.map(quest => {
            if (quest.id !== questId) return quest;

            const updatedGoals = [...quest.goals, goal];
            const { progress, earnedXP, totalXP } = calculateQuestProgress(updatedGoals, state.userLevel, quest.pointRecord);

            return reopenQuest({
              ...quest,
              goals: updatedGoals,
              progress,
              earnedXP,
              totalXP,
              updatedAt: touchQuest(quest),
            });
          });

          return { quests: updatedQuests };
        });
        
        return id;
      },
      
      updateGoal: (questId, goalId, updates) => {
        set((state) => {
          // Update goal
          const updatedQuests = state.quests.map(quest => {
            if (quest.id !== questId) return quest;

            const updatedGoals = quest.goals.map(goal => {
              if (goal.id !== goalId) return goal;
              // Re-pointing a link restarts its attribution window, so the new target's
              // pre-existing history isn't inherited by the goal.
              const linkChanged =
                ('linkType' in updates && updates.linkType !== goal.linkType) ||
                ('linkId' in updates && updates.linkId !== goal.linkId);
              const next = { ...goal, ...updates, updatedAt: new Date().toISOString() };
              if (linkChanged) {
                next.linkStartedAt = next.linkType && next.linkId ? new Date().toISOString() : undefined;
              }
              // Metadata completion is non-paying and does not auto-complete a quest.
              if (updates.completed !== undefined) {
                next.completedAt = next.completed ? (goal.completed ? goal.completedAt ?? new Date().toISOString() : new Date().toISOString()) : undefined;
                next.progress = next.completed ? 100 : undoneGoalProgress(next);
              } else if (next.completed) {
                next.progress = 100;
              }
              return next;
            });

            const { progress, earnedXP, totalXP } = calculateQuestProgress(updatedGoals, state.userLevel, quest.pointRecord);

            return reopenQuest({
              ...quest,
              goals: updatedGoals,
              progress,
              earnedXP,
              totalXP,
              updatedAt: touchQuest(quest),
            });
          });

          return { quests: updatedQuests };
        });
      },
      
      deleteGoal: (questId, goalId) => {
        set((state) => {
          // Delete goal
          const updatedQuests = state.quests.map(quest => {
            if (quest.id !== questId) return quest;

            const updatedGoals = quest.goals.filter(goal => goal.id !== goalId);
            const { progress, earnedXP, totalXP } = calculateQuestProgress(updatedGoals, state.userLevel, quest.pointRecord);

            return {
              ...quest,
              goals: updatedGoals,
              progress,
              earnedXP,
              totalXP,
              updatedAt: touchQuest(quest),
            };
          });

          return { quests: updatedQuests };
        });
      },
      
      toggleGoal: (questId, goalId) => {
        const questBefore = get().quests.find(q => q.id === questId);
        const goalBefore = questBefore?.goals.find(goal => goal.id === goalId);

        set((state) => {
          const quest = state.quests.find(q => q.id === questId);
          if (!quest) return state;

          const goal = quest.goals.find(g => g.id === goalId);
          if (!goal) return state;

          const completed = !goal.completed;

          // Only create a new point record if the goal is being completed and doesn't already have one
          let pointRecord = goal.pointRecord;
          const completedAt = completed ? new Date().toISOString() : undefined;

          if (completed && !pointRecord) {
            // Calculate points when completing - includes both base and per-goal bonus
            const goalPoints = calculateLevelBasedPoints(
              POINTS.QUEST_GOAL_BASE + POINTS.QUEST_PER_GOAL_BONUS,
              state.userLevel
            );
            pointRecord = {
              points: goalPoints,
              userLevel: state.userLevel,
              earnedAt: new Date().toISOString()
            };
          }

          // Update goal
          const updatedGoals = quest.goals.map(g =>
            g.id === goalId
              ? {
                  ...g,
                  completed,
                  completedAt,
                  pointRecord,
                  progress: completed ? 100 : undoneGoalProgress(g),
                  updatedAt: new Date().toISOString()
                }
              : g
          );

          // Check if all goals are completed to auto-complete quest
          const allGoalsCompleted = updatedGoals.every(g => g.completed);
          let questStatus = quest.status;
          let questCompletedAt = quest.completedAt;
          let questPointRecord = quest.pointRecord;

          if (allGoalsCompleted && quest.status === 'active') {
            questStatus = 'completed';
            questCompletedAt = new Date().toISOString();

            // Calculate quest completion bonus: BaseBonus only
            // (Per-goal bonuses are already included in goal completion XP)
            const baseBonus = calculateLevelBasedPoints(POINTS.QUEST_BASE_BONUS, state.userLevel);
            const completionPoints = baseBonus;

            if (!questPointRecord) questPointRecord = {
              points: completionPoints,
              userLevel: state.userLevel,
              earnedAt: new Date().toISOString()
            };
          }

          // Calculate quest progress with the potentially updated questPointRecord
          const { progress, earnedXP, totalXP } = calculateQuestProgress(updatedGoals, state.userLevel, questPointRecord);

          // Update quest
          const updatedQuests = state.quests.map(q =>
            q.id === questId
              ? reopenQuest({
                  ...q,
                  goals: updatedGoals,
                  progress,
                  earnedXP,
                  totalXP,
                  status: questStatus,
                  completedAt: questCompletedAt,
                  pointRecord: questPointRecord,
                  updatedAt: touchQuest(q),
                })
              : q
          );

          return { quests: updatedQuests };
        });

        // Log activity for goal completion
        const updatedQuest = get().quests.find(q => q.id === questId);
        const updatedGoal = updatedQuest?.goals.find(g => g.id === goalId);

        if (updatedGoal && updatedGoal.completed && updatedGoal.pointRecord && !goalBefore?.pointRecord) {
          useActivityLogStore.getState().addActivity({
            activityType: 'quest',
            activityName: `${updatedQuest!.title} - ${updatedGoal.actionVerb} ${updatedGoal.what}`,
            domainId: updatedQuest!.category,
            xpEarned: updatedGoal.pointRecord.points,
            metadata: {
              questId: updatedQuest!.id,
              questName: updatedQuest!.title,
              goalId: updatedGoal.id,
              goalTitle: `${updatedGoal.actionVerb} ${updatedGoal.what}`
            },
            completedAt: updatedGoal.completedAt || new Date().toISOString()
          });
        }

        // Publish quest completion only when this action created its first record.
        if (updatedQuest && updatedQuest.status === 'completed' && updatedQuest.pointRecord && !questBefore?.pointRecord) {
          // pacted quest completed via its last goal — publish the check-in
          void autoCheckInPacts({ type: 'quest', id: updatedQuest.id });
          useActivityLogStore.getState().addActivity({
            activityType: 'quest',
            activityName: updatedQuest.title,
            domainId: updatedQuest.category,
            xpEarned: updatedQuest.pointRecord.points,
            metadata: {
              questId: updatedQuest.id,
              questCompleted: true,
              totalGoals: updatedQuest.goals.length
            },
            completedAt: updatedQuest.completedAt || new Date().toISOString()
          });

          useUpgradesStore.getState().checkQuestUnlocks(updatedQuest.id);
        }
      },
      
      updateGoalProgress: (questId, goalId, progress) => {
        set((state) => {
          // Update goal progress
          const updatedQuests = state.quests.map(quest => {
            if (quest.id !== questId) return quest;

            const updatedGoals = quest.goals.map(goal =>
              goal.id === goalId
                ? {
                    ...goal,
                    progress: goal.completed ? 100 : Math.min(goal.linkType && goal.linkId ? 99 : 100, Math.max(0, progress)),
                    updatedAt: new Date().toISOString()
                  }
                : goal
            );

            const { progress: questProgress, earnedXP, totalXP } = calculateQuestProgress(updatedGoals, state.userLevel, quest.pointRecord);

            return {
              ...quest,
              goals: updatedGoals,
              progress: questProgress,
              earnedXP,
              totalXP,
              updatedAt: touchQuest(quest),
            };
          });

          return { quests: updatedQuests };
        });
      },
      
      syncLinkedGoals: (sources) => {
        // Pass 1 — pure read: work out which linked goals moved.
        const advancing: Array<{ questId: string; goalId: string }> = [];
        const progressUpdates: Array<{ questId: string; goalId: string; progress: number }> = [];

        get().quests.forEach(quest => {
          if (quest.status !== 'active') return;
          quest.goals.forEach(goal => {
            if (goal.completed) return;            // never un-complete; XP is never clawed back
            if (!goal.linkType || !goal.linkId) return;

            const derived = deriveLinkedGoalState(goal, sources);
            if (!derived) return;                  // not derivable → leave the manual toggle alone

            if (derived.completed) {
              advancing.push({ questId: quest.id, goalId: goal.id });
            } else if (Math.min(99, derived.progress) !== goal.progress) {
              progressUpdates.push({ questId: quest.id, goalId: goal.id, progress: Math.min(99, derived.progress) });
            }
          });
        });

        if (!advancing.length && !progressUpdates.length) return;

        // Pass 2 — partial progress, applied in one batch.
        if (progressUpdates.length) {
          set((state) => ({
            quests: state.quests.map(quest => {
              const mine = progressUpdates.filter(u => u.questId === quest.id);
              if (!mine.length) return quest;
              const updatedGoals = quest.goals.map(goal => {
                const update = mine.find(u => u.goalId === goal.id);
                return update ? { ...goal, progress: update.progress, updatedAt: new Date().toISOString() } : goal;
              });
              const { progress, earnedXP, totalXP } = calculateQuestProgress(updatedGoals, state.userLevel, quest.pointRecord);
              return { ...quest, goals: updatedGoals, progress, earnedXP, totalXP, updatedAt: touchQuest(quest) };
            })
          }));
        }

        // Pass 3 — completions go through toggleGoal so XP mints exactly once and the
        // activity log, quest auto-completion, pact check-in and unlock checks all fire.
        advancing.forEach(({ questId, goalId }) => {
          const goal = get().quests.find(q => q.id === questId)?.goals.find(g => g.id === goalId);
          if (goal && !goal.completed) get().toggleGoal(questId, goalId);
        });
      },

      // Getters
      getQuestsByFolder: (folderId) => {
        return get().quests.filter(quest => quest.folderId === folderId);
      },
      
      getActiveQuests: () => {
        return get().quests.filter(quest => quest.status === 'active');
      },
      
      getCompletedQuests: () => {
        return get().quests.filter(quest => quest.status === 'completed');
      },
      
      getArchivedQuests: () => {
        return get().quests.filter(quest => quest.status === 'archived');
      }
    }),
    {
      name: 'quests-storage',
      // ⭐ One saved copy per account — see `scopeToAccount` below.
      storage: accountStorage(cachedUserIdOwner),
      version: 1,
      // initializedUserId stays transient so load-on-login runs each start.
      partialize: (state: QuestState) => ({
        folders: state.folders,
        quests: state.quests,
        // Must persist: a tombstone that dies on reload lets the next merge
        // resurrect the record it was suppressing.
        deleted: state.deleted,
        userLevel: state.userLevel,
        cachedUserId: state.cachedUserId,
      }) as any,
      onRehydrateStorage: () => (state) => {
        // Seed the built-in folders + collapse any duplicates in the local cache.
        // (Stable system ids mean this is idempotent and never stacks copies.)
        if (state) {
          const reconciled = reconcileQuestFolders(state.folders, state.quests);
          state.folders = reconciled.folders;
          state.quests = reconciled.quests;
        }
      }
    }
  )
);

// ⚠️⚠️ Every action that changes a quest — INCLUDING its nested goals — must stamp
// the QUEST's updatedAt. The blob merge compares quest.updatedAt (cloud wins a tie)
// and `matchesCloud` compares id@updatedAt, so an unstamped change is never written
// and the next load restores the cloud copy (found 2026-09-17: goal edits, toggles,
// progress and archive/restore were all lost on reload).
// Auto-sync to Supabase whenever folders/quests change (any of the 16 data
// mutations). Reference-equality check skips non-data changes (e.g. userLevel).
// No suppression flag: initialize() reuses the previous references when its merge
// changed nothing, and a write-back when it DID change something is what converges.
useQuestStore.subscribe((state, prev) => {
  if (swappingAccount()) return; // a change of account swaps the saved copy in — not an edit
  if (
    state.folders === prev.folders &&
    state.quests === prev.quests &&
    state.deleted === prev.deleted
  ) return;
  questSync.schedule();
});

/* ⚠️⚠️ ONE SAVED COPY PER ACCOUNT (lib/accountStorage). The device kept one copy, tagged with the
   account it was loaded for, and a sign-out does not reload the page: an account's edits whose
   write had not landed lived only until the next account's load replaced the copy, and the next
   account's edits made before its own load were merged into the last one's row by ITS next load. */
scopeToAccount(useQuestStore);
